import * as React from 'react';
import { useRef, useState } from 'react';

import { BaseInput } from '../../cdl/Input/components/BaseInput';
import { InputContainer } from '../../cdl/Input/components/InputContainer';
import { translate } from '../helpers/translate.helper';
import { useRole } from '../hooks/useRole';
import { IconSearch } from '../icons/cdl/Search';
import styled from 'styled-components';
import { useMatch, useNavigate } from '@tanstack/react-router';
import { joinWithComma } from '../helpers/joinWithComma.helper';
import { theme } from '../ui/theme';

const GlobalSearchInputContainer = styled(InputContainer)`
    background-color: transparent;
    box-shadow: none;
    transition: box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
        box-shadow: ${joinWithComma(theme.shadows.select.hover, theme.shadows.border.default)};
    }

    ${(props) =>
        props.$isFocused &&
        `
        box-shadow: ${joinWithComma(theme.shadows.focus, theme.shadows.border.default)};

        &:hover {
            box-shadow: ${joinWithComma(theme.shadows.focus, theme.shadows.select.hover, theme.shadows.border.default)};
        }
    `}
`;

const SimpleButton = styled.button`
    border: none;
    padding: 0;
    margin-left: 10px;
    color: ${theme.colors.foreground.subtle};
`;

const useRoleSpecificPlaceholder = () => {
    const role = useRole();

    const getTranslationKey = () => {
        if (role.isSupplier()) {
            return 'globalsearch.placeholder.supplier';
        }
        if (role.isCustomer()) {
            return 'globalsearch.placeholder.customer';
        }
        if (role.isSuperAdmin()) {
            return 'globalsearch.placeholder.admin';
        }
    };

    const translationKey = getTranslationKey();

    return translationKey ? translate(translationKey) : '';
};

export const GlobalSearch = () => {
    const navigate = useNavigate();
    const searchRouteMatch = useMatch({ from: '/_app/search', shouldThrow: false });
    const role = useRole();
    const [draftQuery, setDraftQuery] = useState('');
    const placeholder = useRoleSpecificPlaceholder();
    const [isInputFocused, setIsInputFocused] = useState(false);
    const routeQuery = searchRouteMatch?.search.query ?? '';
    const query = searchRouteMatch ? (isInputFocused ? draftQuery : routeQuery) : draftQuery;

    const searchInputRef = useRef(null);

    const onSearchSubmit = (event) => {
        event.preventDefault();
        const nextQuery = query.trim();

        if (searchInputRef.current) {
            // focus the input as a convenience for the user
            searchInputRef.current.focus();
        }

        if (nextQuery) {
            navigate({ to: '/search', search: { query: nextQuery } });
        }
    };

    return (
        <form onSubmit={onSearchSubmit} width="100%" height="100%">
            <GlobalSearchInputContainer $isFocused={isInputFocused}>
                <SimpleButton type="submit">
                    <IconSearch height={16} width={16} />
                </SimpleButton>

                <BaseInput
                    ref={searchInputRef}
                    $hasLeadingVisual
                    onChange={(e) => setDraftQuery(e.target.value)}
                    value={query}
                    placeholder={placeholder}
                    onFocus={() => {
                        if (searchRouteMatch) {
                            setDraftQuery(routeQuery);
                        }
                        setIsInputFocused(true);
                    }}
                    onBlur={() => setIsInputFocused(false)}
                />
            </GlobalSearchInputContainer>
        </form>
    );
};
